import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    VStack,
    HStack,
    Text,
    IconButton,
    useColorModeValue,
    SkeletonText, useBreakpointValue,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import OutlineButton from "./OutlineButton";
import axios from "axios";
import { useServerIP } from "../../redux/ServerIPContext";
import { addAudioInAudiobook } from "../../redux/reducers";
import {useDispatch, useSelector} from "react-redux";
import AudioCombiner from "../../pages/PDFPage/AudioCombiner";
import {createAudiobook, getOutline} from "../../utils/audiobook-helpers";
import StreamingLoader from "./StreamingLoader";

const TranscriptViewer = ({ audiobookOutline, fileID, userID, socketID = null, socket = null, isAutoScrollEnabled }) => {
    const [isOutlineOpen, setIsOutlineOpen] = useState(true);
    // pageTranscripts: { [page]: { text, segments } }
    const [pageTranscripts, setPageTranscripts] = useState({});
    // Array of audio URLs from each page.
    const [pageAudioURLs, setPageAudioURLs] = useState([]);
    // Store durations for pages that only have abridged text
    const [pageAudioDurations, setPageAudioDurations] = useState({});
    // Current audio time (in seconds) from the combined audio.
    const [currentAudioTime, setCurrentAudioTime] = useState(0);
    const dispatch = useDispatch();
    const transcriptRef = useRef(null);
    const headingRefs = useRef({});
    const { serverIPs } = useServerIP();
    // Ref to control AudioPlayer (via AudioCombiner).
    const audioPlayerRef = useRef(null);
    // Ref for each transcript segment element.
    const transcriptSegmentRefs = useRef({});
    const [combinedDuration, setCombinedDuration] = useState(0);
    const currentPageAudiobook = useSelector(state => state.settings.currentPageAudiobook);
    const [loadingMessage, setLoadingMessage] = useState("");
    const breakpoint = useBreakpointValue({ base: 'base', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' });
    const isMobile = breakpoint === 'base' || breakpoint === 'sm';
    const outlineSideWidth = isMobile ? "42%" : "250px";

    // For controlling when to recalculate the scaling ratio
    const lastPageCount = useRef(0);
    const BATCH_SIZE = 5;
    const scalingRatio = useRef(1);

    const addAudioURL = (newURL) => {
        setPageAudioURLs((prev) => [...prev, newURL]);
    };

    /**
     * Gather all pages from the outline, remove duplicates, and sort them.
     */
    const getAllPagesInOrder = () => {
        const pageSet = new Set();
        audiobookOutline.forEach((chapter) => {
            for (let p = chapter.start_page; p <= chapter.end_page; p++) {
                pageSet.add(p);
            }
        });
        return Array.from(pageSet).sort((a, b) => a - b);
    };

    // Helper function to get audio duration from URL
    const getAudioDuration = (url) => {
        return new Promise((resolve, reject) => {
            const audio = new Audio();
            audio.addEventListener('loadedmetadata', () => {
                resolve(audio.duration);
            });
            audio.addEventListener('error', reject);
            audio.src = url;
        });
    };

    /**
     * Fetch a single page repeatedly until it returns abridged text, audio URL,
     * and optionally an aligned_transcript.
     */
    const fetchSinglePageWithRetry = (page) => {
        return new Promise((resolve) => {
            const attemptFetch = async () => {
                // console.log("FETCHING PAGE", {
                //     file_id: fileID.toString(),
                //     user_id: userID.toString(),
                //     page_number: page,
                //     socketio_stream_room: socketID,
                // });
                try {
                    const response = await axios.post(serverIPs.SERVER_IP + "/audiobooks/get_page", {
                        file_id: fileID.toString(),
                        user_id: userID.toString(),
                        page_number: page,
                        socketio_stream_room: socketID,
                    });
                    // console.log("FETCH PAGE RESPONSE", response);

                    // Check if the page is blank.
                    if (
                        response.data.status === 200 &&
                        response.data.message &&
                        response.data.message.message === "Page is blank"
                    ) {
                        // Skip this page by resolving immediately.
                        finishedPages.current.add(page);
                        resolve();
                        return;
                    }

                    const data = response.data?.message || {};
                    const abridgedText = data.abridged_page_text;
                    const audioUrl = data.audio_page_wav_url;
                    // If aligned_transcript exists, use it; otherwise, we only have abridged text.
                    if (abridgedText && audioUrl) {
                        if (data.aligned_transcript) {
                            setPageTranscripts((prev) => ({
                                ...prev,
                                [page]: { segments: data.aligned_transcript },
                            }));
                        } else {
                            setPageTranscripts((prev) => ({
                                ...prev,
                                [page]: { text: abridgedText },
                            }));
                        }
                        addAudioURL(audioUrl);
                        // console.log("ADDED AUDIO URL", audioUrl);
                        dispatch(addAudioInAudiobook({ documentID: fileID, audioToAdd: audioUrl }));
                        resolve();
                    } else {
                        setTimeout(attemptFetch, 5000);
                    }
                } catch (error) {
                    console.error("Error fetching page", page, error);
                    setTimeout(attemptFetch, 5000);
                }
            };
            attemptFetch();
        });
    };

    const fetchSinglePageWithRetryAligned = (page) => {
        return new Promise((resolve) => {
            let retryCount = 0;
            const attemptFetch = async () => {
                console.log("FETCHING PAGE ALIGNED", {
                    file_id: fileID.toString(),
                    user_id: userID.toString(),
                    page_number: page,
                    socketio_stream_room: socketID,
                });
                try {
                    const response = await axios.post(serverIPs.SERVER_IP + "/audiobooks/get_page", {
                        file_id: fileID.toString(),
                        user_id: userID.toString(),
                        page_number: page,
                        socketio_stream_room: socketID,
                    });
                    console.log("FETCH PAGE RESPONSE ALIGNED", response);

                    // Check if the page is blank.
                    if (
                        response.data.status === 200 &&
                        response.data.message &&
                        response.data.message.message === "Page is blank"
                    ) {
                        // Skip this page by resolving immediately.
                        finishedPages.current.add(page);
                        resolve();
                        return;
                    }

                    const data = response.data?.message || {};
                    const abridgedText = data.abridged_page_text;
                    const audioUrl = data.audio_page_wav_url;
                    // If aligned_transcript exists, use it; otherwise, we only have abridged text.
                    if (abridgedText && audioUrl && data.aligned_transcript) {
                        setPageTranscripts((prev) => ({
                            ...prev,
                            [page]: { segments: data.aligned_transcript },
                        }));
                        resolve();
                    } else {
                        // If no aligned_transcript, check if we've retried 11 times.
                        if (retryCount >= 11) {
                            // We know we'll only have abridged text, so get the audio duration
                            try {
                                const audioDuration = await getAudioDuration(audioUrl);
                                setPageAudioDurations(prev => ({
                                    ...prev,
                                    [page]: audioDuration
                                }));
                            } catch (error) {
                                console.error("Error getting audio duration for page", page, error);
                            }
                            // Fallback: use abridged_page_text and audio_page_wav_url.
                            setPageTranscripts((prev) => ({
                                ...prev,
                                [page]: { text: abridgedText },
                            }));
                            resolve();
                        } else {
                            retryCount++;
                            setTimeout(attemptFetch, 4000);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching page", page, error);
                    if (retryCount >= 11) {
                        // On error after maximum retries, try to get audio duration if we have URL
                        const audioUrl = error.response?.data?.message?.audio_page_wav_url;
                        if (audioUrl) {
                            try {
                                const audioDuration = await getAudioDuration(audioUrl);
                                setPageAudioDurations(prev => ({
                                    ...prev,
                                    [page]: audioDuration
                                }));
                            } catch (audioError) {
                                console.error("Error getting audio duration for page", page, audioError);
                            }
                        }
                        // Fallback to abridged text
                        setPageTranscripts((prev) => ({
                            ...prev,
                            [page]: { text: error.response?.data?.message?.abridged_page_text || "Error retrieving text" },
                        }));
                        resolve();
                    } else {
                        retryCount++;
                        setTimeout(attemptFetch, 4000);
                    }
                }
            };
            attemptFetch();
        });
    };

    /**
     * Fetch pages sequentially in order.
     */
    const fetchAllPagesSequentially = async () => {
        const pagesInOrder = getAllPagesInOrder();
        for (let i = 0; i < pagesInOrder.length; i++) {
            const page = pagesInOrder[i];
            if (!pageTranscripts[page]) {
                await fetchSinglePageWithRetry(page);
            }
        }
    };

    // useEffect(() => {
    //     // console.log("Updated AUDIO URLS", pageAudioURLs);
    // }, [pageAudioURLs]);

    useEffect(() => {
        fetchAllPagesSequentially();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const scrollToHeading = (heading) => {
        if (headingRefs.current[heading]) {
            headingRefs.current[heading].scrollIntoView({ behavior: "smooth", block: "start" });

            // Find the chapter that matches this heading
            const chapter = audiobookOutline.find(ch => ch.title === heading);
            if (chapter) {
                // Find the first segment in this chapter with a valid globalStart time
                const firstSegment = adjustedTranscriptElements.find(seg => {
                    const pageNum = parseInt(seg.id.split('-')[1]);
                    return pageNum >= chapter.start_page && pageNum <= chapter.end_page && seg.globalStart !== null;
                });

                if (firstSegment && audioPlayerRef.current) {
                    audioPlayerRef.current.seekTo(firstSegment.globalStart);
                    if (typeof audioPlayerRef.current.playAudio === "function") {
                        audioPlayerRef.current.playAudio();
                    }
                }
            }
        }
    };

    // When a transcript sentence is clicked, seek the audio to that segment's global start.
    const handleSegmentClick = (globalStart) => {
        if (audioPlayerRef.current && typeof audioPlayerRef.current.seekTo === "function") {
            audioPlayerRef.current.seekTo(globalStart);
        }
        // Ensure audio plays.
        if (typeof audioPlayerRef.current.playAudio === "function") {
            audioPlayerRef.current.playAudio();
        }
    };

    // Build transcript elements with global timestamps.
    // For pages with aligned transcript, compute the global start/end by adding a cumulative offset.
    const pagesOrdered = getAllPagesInOrder();
    let cumulativeOffset = 0;
    const transcriptElements = [];
    pagesOrdered.forEach((page) => {
        const pageData = pageTranscripts[page];
        if (!pageData) return; // Skip pages not yet loaded.
        if (pageData.segments) {
            // For pages with an aligned transcript, each segment's times are relative to this page.
            pageData.segments.forEach((segment, index) => {
                transcriptElements.push({
                    id: `page-${page}-seg-${index}`,
                    text: segment.text,
                    // Global times are computed as cumulativeOffset + segment's relative time.
                    globalStart: cumulativeOffset + segment.start_time,
                    globalEnd: cumulativeOffset + segment.end_time,
                });
            });
            // Update cumulativeOffset based on this page's duration (assume last segment's end_time).
            const lastSegment = pageData.segments[pageData.segments.length - 1];
            cumulativeOffset += lastSegment.end_time;
        } else if (pageData.text) {
            // If no aligned transcript, render the whole text without timing.
            transcriptElements.push({
                id: `page-${page}`,
                text: pageData.text,
                globalStart: null,
                globalEnd: null,
            });
        }
    });

    // Determine active segment: one with timing and currentAudioTime within its bounds.
    // Compute total transcript duration (from cumulativeOffset) and adjust each segment's timestamps.
    const totalTranscriptDuration = cumulativeOffset;
    let adjustedTranscriptElements = transcriptElements;

    // Calculate additional duration from non-aligned pages
    const additionalDuration = Object.values(pageAudioDurations).reduce((sum, duration) => sum + duration, 0);
    const totalDurationWithNonAligned = totalTranscriptDuration + additionalDuration;

    // Only recalculate the scaling ratio every BATCH_SIZE pages or on final load
    const currentPageCount = Object.keys(pageTranscripts).length;
    const allPages = getAllPagesInOrder();
    const isCompleted = currentPageCount === allPages.length;

    if (combinedDuration && totalDurationWithNonAligned > 0 &&
        (currentPageCount >= lastPageCount.current + BATCH_SIZE || isCompleted)) {

        scalingRatio.current = combinedDuration / totalDurationWithNonAligned;
        lastPageCount.current = currentPageCount;
    }

    // Apply the scaling ratio
    adjustedTranscriptElements = transcriptElements.map((seg) => ({
        ...seg,
        globalStart: seg.globalStart != null ? seg.globalStart * scalingRatio.current : null,
        globalEnd: seg.globalEnd != null ? seg.globalEnd * scalingRatio.current : null,
    }));

    const activeSegmentId = adjustedTranscriptElements.find(
        (seg) =>
        seg.globalStart != null &&
        seg.globalEnd != null &&
        currentAudioTime >= seg.globalStart &&
        currentAudioTime < seg.globalEnd
    )?.id;

    // Auto-scroll the active transcript block into view.
    useEffect(() => {
        if (
            activeSegmentId &&
            transcriptSegmentRefs.current[activeSegmentId] && isAutoScrollEnabled
        ) {
            transcriptSegmentRefs.current[activeSegmentId].scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [activeSegmentId]);

    // Maintain a ref to track finished pages.
    const finishedPages = useRef(new Set());

    // Helper: check that all pages lower than the given page (starting at 1)
    // are finished. (Adjust this if your page numbering or logic is different.)
    const arePreviousPagesFinished = (page) => {
        for (let p = 1; p < page; p++) {
            if (!finishedPages.current.has(p)) {
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        // socketio.emit('transcribe_audio_for_pages', {'progress': f"Finished creating aligned transcripts for pages {pages}"}, room=socketio_stream_room)
        socket.on('transcribe_audio_for_pages', function (data) {
            if (data.progress.includes("Finished creating aligned transcripts for pages")){
                console.log("TRANSCRIPT VIEWER UPDATES", data);
                // Extract pages from the progress message.
                const match = data.progress.match(/\[(.*?)\]/);
                if (match) {
                    const pagesStr = match[1]; // e.g. "1, 2, 4, 5, 8, 9, 11, 13" or "3"
                    setLoadingMessage("Created transcript for pages " + pagesStr)
                    let pages = pagesStr
                        .split(",")
                        .map(s => parseInt(s.trim(), 10))
                        .sort((a, b) => a - b);
                    // Filter out pages that have already finished.
                    pages = pages.filter(page => !finishedPages.current.has(page));
                    // Process pages sequentially.
                    (async () => {
                        for (const page of pages) {
                            // Wait until all previous pages are finished.
                            while (!arePreviousPagesFinished(page)) {
                                await new Promise(r => setTimeout(r, 1000));
                            }
                            // Fetch this page aligned transcript (retrying every 5 sec until finished).
                            await fetchSinglePageWithRetryAligned(page);
                            // Mark this page as finished.
                            finishedPages.current.add(page);
                            // Check if this was the last page
                            const allPages = getAllPagesInOrder();
                            const allPagesFinished = allPages.every(p => finishedPages.current.has(p));
                            if (allPagesFinished) {
                                setLoadingMessage("");
                            }
                        }
                    })();
                }
            }

            if (data.progress.includes("Starting to transcribe audio for pages")) {
                setLoadingMessage("Starting to transcribe audio for pages");
            }

            if (data.progress.includes("page") && data.progress.includes("finished")) {
                // Update loadingMessage for the new event type.
                setLoadingMessage(data.progress);
            }
            if (data.progress.includes("Finished audio generation for pages")) {
                // Update loadingMessage for the new event type.
                setLoadingMessage(data.progress);
            }


        });

        socket.on('audiobook_generate_starting_outline_audio_chunks', function (data) {
            if (data.progress.includes("Starting to make pages readable")) {
                setLoadingMessage(data.progress);
            }
            if (data.progress.includes("Finished making pages readable")) {
                setLoadingMessage(data.progress);
            }
            if (data.progress.includes("finished audio, finishing up")) {
                setLoadingMessage(data.progress);
            }
            if (data.progress.includes("Outline has been created")) {
                setLoadingMessage(data.progress);
            }
            if (data.progress.includes("processing section")) {
                setLoadingMessage(data.progress);
            }
            if (data.progress.includes("generating audio chunks")) {
                setLoadingMessage(data.progress);
            }
            if (data.progress.includes("page") && data.progress.includes("finished")) {
                setLoadingMessage(data.progress);
            }

        });
        return () => {
            console.log("Socket disconnecting", socket)
            socket.disconnect();
        }
    }, []);

    return (
        <Box display="flex" height="100%">
            {/* Sidebar Outline Panel */}
            {loadingMessage !== "" ?
            <StreamingLoader message={loadingMessage} /> : null}
            <Box
                width={isOutlineOpen ? outlineSideWidth : "50px"}
                transition="width 0.3s ease-in-out"
                bg={useColorModeValue("gray.100", "gray.700")}
                p={2}
                boxShadow="md"
                minWidth="50px"
                overflow="visible"
                position="relative"
            >
                <HStack justify="space-between">
                    {isOutlineOpen && <Text fontWeight="bold" ml={2}>Outline</Text>}
                    <IconButton
                        aria-label="Toggle Sidebar"
                        icon={isOutlineOpen ? <FaChevronLeft /> : <FaChevronRight />}
                        size="sm"
                        onClick={() => setIsOutlineOpen(!isOutlineOpen)}
                    />
                </HStack>
                <VStack align="start" spacing={2} mt={4} position="relative">
                    {audiobookOutline.map((chapter, index) => (
                        <OutlineButton
                            key={index}
                            heading={chapter.title}
                            onClick={() => scrollToHeading(chapter.title)}
                        />
                    ))}
                </VStack>
            </Box>


            <Box flex="1" overflowY="auto" p={6} px={!isMobile ? 6 : 3} ref={transcriptRef} bg="white">
                <VStack align="start" spacing={4}>
                    {audiobookOutline.map((chapter) => (
                        <Box key={chapter.title} width="100%">
                            <Text
                                ref={(el) => (headingRefs.current[chapter.title] = el)}
                                fontSize="xl"
                                fontWeight="bold"
                                color="orange.500"
                                mb={2}
                            >
                                {chapter.title} (Pages {chapter.start_page} – {chapter.end_page})
                            </Text>
                            {Array.from(
                                { length: chapter.end_page - chapter.start_page + 1 },
                                (_, idx) => {
                                    const pageNum = chapter.start_page + idx;
                                    if (!pageTranscripts[pageNum]) {
                                        return (
                                            <SkeletonText key={pageNum} width="100%" spacing="4" />
                                        );
                                    } else if (pageTranscripts[pageNum].segments) {
                                        // The ratio is now handled at the component level rather than recalculated here
                                        const ratio = scalingRatio.current;

                                        return (
                                            <VStack key={pageNum} align="start" spacing="2">
                                                {pageTranscripts[pageNum].segments.map((segment, i) => {
                                                    // Find this segment in the adjusted transcript elements
                                                    const segmentId = `page-${pageNum}-seg-${i}`;
                                                    const adjustedSegment = adjustedTranscriptElements.find(s => s.id === segmentId);

                                                    // Use the adjusted timing if available
                                                    const globalStart = adjustedSegment ? adjustedSegment.globalStart : null;
                                                    const globalEnd = adjustedSegment ? adjustedSegment.globalEnd : null;

                                                    const isActive = activeSegmentId === segmentId;

                                                    return (
                                                        <Text
                                                            key={segmentId}
                                                            ref={(el) => (transcriptSegmentRefs.current[segmentId] = el)}
                                                            whiteSpace="pre-wrap"
                                                            p={2}
                                                            borderRadius="md"
                                                            cursor="pointer"
                                                            bg={isActive ? "yellow.200" : "transparent"}
                                                            _hover={{ bg: isActive ? undefined : "yellow.100" }}
                                                            onClick={() => globalStart !== null && handleSegmentClick(globalStart)}
                                                        >
                                                            {segment.text}
                                                        </Text>
                                                    );
                                                })}
                                            </VStack>
                                        );
                                    } else if (pageTranscripts[pageNum].text) {
                                        return (
                                            <SkeletonText key={pageNum} isLoaded={true} width="100%" spacing="4">
                                                <Text whiteSpace="pre-wrap">
                                                    {pageTranscripts[pageNum].text}
                                                </Text>
                                            </SkeletonText>
                                        );
                                    }
                                }
                            )}
                        </Box>
                    ))}
                </VStack>
            </Box>

            {/* Audio Combiner/Player */}
            <Box
                position="absolute"
                bottom="0px"
                left="50%"
                transform="translateX(-50%)"
                zIndex="1000"
                width="auto"
            >
                <AudioCombiner
                    pageAudioURLs={pageAudioURLs}
                    onTimeUpdate={setCurrentAudioTime}
                    onDuration={setCombinedDuration}
                    forwardedRef={audioPlayerRef}
                />
            </Box>
        </Box>
    );
};

export default TranscriptViewer;
